import React, { FC, RefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import Script from 'next/script';
import { ThemeProvider } from 'styled-components';

import { defaultErrorHandler, init } from '@r1-frontend/analytics/campaignLayer';

import { lightTheme } from '@r1-frontend/ui-kit/themes';
import { isClientSide } from '@r1-frontend/shared/helpers/ssr';

import { IS_GTM_DISABLED } from '~/src/constants';
import App from '~/src/features/_layout/App';
import Head, { IHead } from '~/src/features/_layout/Head';
import { useHeaderScroll } from '~/src/features/_layout/Header/useHeaderScroll';
import { PassportUpdate } from '~/src/features/lk/PassportUpdate';
import { TState } from '~/src/store';
import { verifyAuthState } from '~/src/store/actions/auth/verifyAuthState';

import { AppContext } from './App/AppContext';

const ymId = 30080914;

const NO_SCROLL_PATH = [
    '/internet',
    '/internet/mono-village',
    '/full-buy',
];

const CookieAlert = dynamic(() => import('@r1-frontend/ui-react/organisms/CookieAlert'), { ssr: false });

interface ILayout extends IHead {
    withMenu?: boolean,
    withFooter?: boolean,
    withChat?: boolean,
    withBottomSticky?: RefObject<HTMLDivElement>,
    bgColor?: string,
    withEmailWidget?: boolean,
    emailWidgetText?: string,
    withShadowInMobileHeader?: boolean,
    withHeaderOffset?: boolean,
}

const Layout: FC<ILayout> = ({
     children,
     withMenu = true,
     withFooter = true,
     withChat = true,
     bgColor,
     withShadowInMobileHeader = true,
     ...props
}) => {
    const isClient = isClientSide();

    const dispatch = useDispatch();

    const { isAuth, clientPersonal } = useSelector((state: TState) => state.auth);
    const { agreement } = clientPersonal || {};

    useEffect(() => {
        const checkCookieInterval = setInterval(() => {
            dispatch(verifyAuthState());
        }, 1000);

        return () => {
            clearInterval(checkCookieInterval);
        };
    }, [dispatch]);

    useEffect(() => {
        if (!agreement) {
            return;
        }

        if (isAuth && agreement) {
            const intervalId = setInterval(() => {
                window.ym && window.ym(
                    ymId,
                    'userParams',
                    { UserID: agreement },
                );
                clearInterval(intervalId);
            }, 1000);

            return () => {
                clearInterval(intervalId);
            };
        }
    }, [isAuth, agreement]);

    useEffect(() => {
        if (isClient && agreement) {
            init({
                agreementNumber: agreement,
                onError: defaultErrorHandler,
                environment: process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? 'production' : 'sandbox',
                debug: process.env.NEXT_PUBLIC_ENVIRONMENT === 'localhost',
            });
        }
    }, []);

    const { isScrollingUp } = useHeaderScroll();

    return (
        <ThemeProvider theme={lightTheme}>
            <Head {...props} />
            <AppContext.Provider value={{ withShadowInMobileHeader, isScrollingUp }}>
                <App
                    bgColor={bgColor}
                    withMenu={withMenu}
                    withFooter={withFooter}
                    withChat={withChat}
                    {...props}
                >
                    <CookieAlert/>
                    <PassportUpdate/>

                    {isClient && (
                        IS_GTM_DISABLED
                            ? <Script async src='/static/js/gtmDevStub.js' />
                            : <>
                                <Script async src='/static/js/googleTagManager.js' />
                                <Script async src='/static/js/googleAnalytics.js' />
                                <Script async src='/static/js/objectFitImages.js' />
                                <Script async src='/static/js/mailRuCounter.js' />
                            </>
                    )}
                    <Script id='disable-speedy'>window.SC_DISABLE_SPEEDY = true</Script>
                    {children}
                </App>
            </AppContext.Provider>
        </ThemeProvider>
    );
};

// TODO Проверить актуальность этого решения. Меняет поведение роутинга по дефолту, сложно найти
Router.events.on('routeChangeComplete', (pathname) => {
    const [path] = pathname.split('?');
    if (NO_SCROLL_PATH.includes(path)) {
        return;
    }
    window.scrollTo(0, 0);
});

export default Layout;
